import { useState } from 'react';

import type { OverrideValue } from '../channel/ChannelSettingDropdown';
import {
  DEFAULT_SUPPORT_MESSAGE_NOT_FOUND,
  DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL,
} from '@core/const/const.DEFAULT_SUPPORT_MESSAGES';
import { PROMPT_ANSWER_STYLE } from '@core/const/const.PROMPT_ANSWER_STYLE';
import { PROMPT_CLASSIFY_IGNORED } from '@core/const/const.PROMPT_CLASSIFY_IGNORED';
import type { DBChannel, DBTeam } from '@core/types/types.db';

export function useChannelSettingsForm(channel: DBChannel, team: DBTeam) {
  // Computed initial values
  const teamDefault = team?.prompt_answer_style ?? PROMPT_ANSWER_STYLE;
  const channelOverride = channel.prompt_answer_style;
  const hasInstructionsOverride = !!channelOverride;

  const channelContext = channel.prompt_context ?? '';
  const hasContextOverride = !!channelContext;

  const channelQuestionsToIgnore = channel.prompt_classify_ignored ?? '';
  const hasQuestionsToIgnoreOverride = !!channelQuestionsToIgnore;
  const teamQuestionsToIgnore =
    team?.prompt_classify_ignored ?? PROMPT_CLASSIFY_IGNORED;

  const teamContextExcluded = !!channel.team_context_disabled;

  const teamSupportMessageNotFound =
    team?.labels?.support_message_not_found ||
    DEFAULT_SUPPORT_MESSAGE_NOT_FOUND;
  const teamSupportMessageNotHelpful =
    team?.labels?.support_message_not_helpful ||
    DEFAULT_SUPPORT_MESSAGE_NOT_HELPFUL;

  const channelSupportMessageNotFound =
    channel.labels?.support_message_not_found;
  const channelSupportMessageNotHelpful =
    channel.labels?.support_message_not_helpful;

  const hasNotFoundLabelOverride = !!channelSupportMessageNotFound;
  const hasNotHelpfulLabelOverride = !!channelSupportMessageNotHelpful;

  // Helper functions
  const getSupportModeDropdownValue = (): OverrideValue => {
    if (
      channel.support_mode_override === null ||
      channel.support_mode_override === undefined
    ) {
      return 'global';
    }
    return channel.support_mode_override ? 'enabled' : 'disabled';
  };

  const getRespondToBotMessagesDropdownValue = (): OverrideValue => {
    if (
      channel.respond_to_bot_messages_override === null ||
      channel.respond_to_bot_messages_override === undefined
    ) {
      return 'global';
    }
    return channel.respond_to_bot_messages_override ? 'enabled' : 'disabled';
  };

  const getAiAnswerDropdownValue = (): OverrideValue => {
    if (
      channel.ai_answer_override === null ||
      channel.ai_answer_override === undefined
    ) {
      return 'global';
    }
    return channel.ai_answer_override ? 'enabled' : 'disabled';
  };

  const getRecencyBiasDropdownValue = (): OverrideValue => {
    if (
      channel.recency_bias_override === null ||
      channel.recency_bias_override === undefined
    ) {
      return 'global';
    }
    return channel.recency_bias_override ? 'enabled' : 'disabled';
  };

  const getCategorySelectionType = () => {
    if (channel.answer_source_category_ids === undefined) return 'all';
    if (channel.answer_source_category_ids.length === 0) return 'none';
    return 'specific';
  };

  // Form state
  const [instructionsOverride, setInstructionsOverride] = useState(
    hasInstructionsOverride,
  );
  const [instructionsValue, setInstructionsValue] = useState(
    channelOverride || teamDefault,
  );

  const [contextOverride, setContextOverride] = useState(hasContextOverride);
  const [contextValue, setContextValue] = useState(channelContext);

  const [questionsToIgnoreOverride, setQuestionsToIgnoreOverride] = useState(
    hasQuestionsToIgnoreOverride,
  );
  const [questionsToIgnoreValue, setQuestionsToIgnoreValue] = useState(
    channelQuestionsToIgnore || teamQuestionsToIgnore,
  );

  const [teamContextToggle, setTeamContextToggle] =
    useState(teamContextExcluded);

  const [supportModeValue, setSupportModeValue] = useState(
    getSupportModeDropdownValue(),
  );

  const [respondToBotMessagesValue, setRespondToBotMessagesValue] = useState(
    getRespondToBotMessagesDropdownValue(),
  );

  const [aiAnswerValue, setAiAnswerValue] = useState(
    getAiAnswerDropdownValue(),
  );

  const [recencyBiasValue, setRecencyBiasValue] = useState(
    getRecencyBiasDropdownValue(),
  );

  const [categorySelectionType, setCategorySelectionType] = useState<
    'all' | 'none' | 'specific'
  >(getCategorySelectionType());
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    channel.answer_source_category_ids || [],
  );

  const [notFoundLabelOverride, setNotFoundLabelOverride] = useState(
    hasNotFoundLabelOverride,
  );
  const [notHelpfulLabelOverride, setNotHelpfulLabelOverride] = useState(
    hasNotHelpfulLabelOverride,
  );

  const [supportMessageNotFoundValue, setSupportMessageNotFoundValue] =
    useState(channelSupportMessageNotFound || teamSupportMessageNotFound);

  const [supportMessageNotHelpfulValue, setSupportMessageNotHelpfulValue] =
    useState(channelSupportMessageNotHelpful || teamSupportMessageNotHelpful);

  const [defaultCategoryOverride, setDefaultCategoryOverride] = useState(
    !!channel.default_category_id,
  );
  const [defaultCategoryValue, setDefaultCategoryValue] = useState(
    channel.default_category_id || team.default_category_id,
  );

  // Reset form function
  const resetForm = () => {
    setInstructionsOverride(hasInstructionsOverride);
    setInstructionsValue(channelOverride || teamDefault);
    setContextOverride(hasContextOverride);
    setContextValue(channelContext);
    setQuestionsToIgnoreOverride(hasQuestionsToIgnoreOverride);
    setQuestionsToIgnoreValue(
      channelQuestionsToIgnore || teamQuestionsToIgnore,
    );
    setTeamContextToggle(teamContextExcluded);
    setSupportModeValue(getSupportModeDropdownValue());
    setRespondToBotMessagesValue(getRespondToBotMessagesDropdownValue());
    setNotFoundLabelOverride(hasNotFoundLabelOverride);
    setNotHelpfulLabelOverride(hasNotHelpfulLabelOverride);
    setSupportMessageNotFoundValue(
      channelSupportMessageNotFound || teamSupportMessageNotFound,
    );
    setSupportMessageNotHelpfulValue(
      channelSupportMessageNotHelpful || teamSupportMessageNotHelpful,
    );
    setCategorySelectionType(getCategorySelectionType());
    setSelectedCategories(channel.answer_source_category_ids || []);
    setDefaultCategoryOverride(!!channel.default_category_id);
    setDefaultCategoryValue(
      channel.default_category_id || team.default_category_id,
    );
  };

  // Check if form has changes
  const hasChanges = () => {
    const instructionsChanged =
      instructionsOverride !== hasInstructionsOverride ||
      (instructionsOverride &&
        instructionsValue.trim() !== (channelOverride || teamDefault));

    const contextChanged =
      contextOverride !== hasContextOverride ||
      (contextOverride && contextValue.trim() !== channelContext);

    const questionsToIgnoreChanged =
      questionsToIgnoreOverride !== hasQuestionsToIgnoreOverride ||
      (questionsToIgnoreOverride &&
        questionsToIgnoreValue.trim() !==
          (channelQuestionsToIgnore || teamQuestionsToIgnore));

    const teamContextChanged = teamContextToggle !== teamContextExcluded;

    const supportModeChanged =
      supportModeValue !== getSupportModeDropdownValue();

    const respondToBotMessagesChanged =
      respondToBotMessagesValue !== getRespondToBotMessagesDropdownValue();

    const aiAnswerChanged = aiAnswerValue !== getAiAnswerDropdownValue();

    const recencyBiasChanged =
      recencyBiasValue !== getRecencyBiasDropdownValue();

    const notFoundLabelChanged =
      notFoundLabelOverride !== hasNotFoundLabelOverride ||
      (notFoundLabelOverride &&
        supportMessageNotFoundValue.trim() !==
          (channelSupportMessageNotFound || teamSupportMessageNotFound));

    const notHelpfulLabelChanged =
      notHelpfulLabelOverride !== hasNotHelpfulLabelOverride ||
      (notHelpfulLabelOverride &&
        supportMessageNotHelpfulValue.trim() !==
          (channelSupportMessageNotHelpful || teamSupportMessageNotHelpful));

    const labelsChanged = notFoundLabelChanged || notHelpfulLabelChanged;

    const originalCategoryType = getCategorySelectionType();
    const categoryTypeChanged = categorySelectionType !== originalCategoryType;

    const originalSelectedCategories = channel.answer_source_category_ids || [];
    const categoriesListChanged =
      categorySelectionType === 'specific' &&
      (selectedCategories.length !== originalSelectedCategories.length ||
        selectedCategories.some(
          (id) => !originalSelectedCategories.includes(id),
        ) ||
        originalSelectedCategories.some(
          (id) => !selectedCategories.includes(id),
        ));

    const categorySettingsChanged =
      categoryTypeChanged || categoriesListChanged;

    const defaultCategoryChanged =
      defaultCategoryOverride !== !!channel.default_category_id ||
      (defaultCategoryOverride &&
        defaultCategoryValue !== channel.default_category_id);

    return (
      instructionsChanged ||
      contextChanged ||
      questionsToIgnoreChanged ||
      teamContextChanged ||
      supportModeChanged ||
      respondToBotMessagesChanged ||
      aiAnswerChanged ||
      recencyBiasChanged ||
      labelsChanged ||
      categorySettingsChanged ||
      defaultCategoryChanged
    );
  };

  return {
    // Form state
    formState: {
      instructionsOverride,
      instructionsValue,
      contextOverride,
      contextValue,
      questionsToIgnoreOverride,
      questionsToIgnoreValue,
      teamContextToggle,
      supportModeValue,
      respondToBotMessagesValue,
      aiAnswerValue,
      recencyBiasValue,
      categorySelectionType,
      selectedCategories,
      defaultCategoryOverride,
      defaultCategoryValue,
      notFoundLabelOverride,
      supportMessageNotFoundValue,
      notHelpfulLabelOverride,
      supportMessageNotHelpfulValue,
    },
    // Setters
    setters: {
      setInstructionsOverride,
      setInstructionsValue,
      setContextOverride,
      setContextValue,
      setQuestionsToIgnoreOverride,
      setQuestionsToIgnoreValue,
      setTeamContextToggle,
      setSupportModeValue,
      setRespondToBotMessagesValue,
      setAiAnswerValue,
      setRecencyBiasValue,
      setCategorySelectionType,
      setSelectedCategories,
      setDefaultCategoryOverride,
      setDefaultCategoryValue,
      setNotFoundLabelOverride,
      setSupportMessageNotFoundValue,
      setNotHelpfulLabelOverride,
      setSupportMessageNotHelpfulValue,
    },
    // Computed values
    computed: {
      teamDefault,
      teamQuestionsToIgnore,
      hasChanges: hasChanges(),
    },
    // Actions
    actions: {
      resetForm,
    },
  };
}
